import { Component, Input, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { BaseStepViewComponent } from './../base-step-view.class';
import { DatasetRecordService } from 'app/main/components/dataset/services/dataset-record.service';
import { DatasetActionContainerService, DatasetEvents } from 'app/main/components/dataset/services/dataset-action-container.service';
import { DatasetNavigatorService } from 'app/main/components/dataset/services/dataset-navigator.service';
import { DatasetService } from 'app/main/components/dataset/services/dataset.service';
import { AppService } from 'app/services/app.service';
import { takeUntil, filter } from 'rxjs/operators';
import { FormViewerComponent } from 'app/main/components/form-viewer/form-viewer.component';
import { DatasetMultilanguageContainerService } from 'app/main/components/dataset/services/dataset-multilanguage-container.service';
import { IFormConfig, IStepConfig } from 'app/interfaces';
import { AssistantService } from 'app/main/components/assistant/assistant.service';
import { AssistantContextService } from 'app/main/components/assistant/assistant-context.service';

@Component({
    selector: 'common-i18n-form-step',
    templateUrl: './step.component.html',
    // styleUrls  : ['./step.component.scss']
    providers: [DatasetActionContainerService, DatasetRecordService, DatasetMultilanguageContainerService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommonI18nFormStepComponent extends BaseStepViewComponent implements OnInit, OnDestroy{
	
	@Input() isLastStep = true;
	@Input() stepConfig: IStepConfig;
	@Input() formConfig: IFormConfig;
	@Input() parentDatasetACS: DatasetActionContainerService;
	@Input() parentDatasetRS: DatasetRecordService;
	@ViewChild('formViewer', { static: false }) formViewer: FormViewerComponent;
	formErrors: any;

	/** step dei testi dell'entità aperta con l'assistente (l'ancoraggio lo fa il contenitore dalla config dello step) */
	private assistantSnapshot = false;
	private assistantService = inject(AssistantService);
	private assistantContextService = inject(AssistantContextService);

	constructor(
		public appService: AppService,
		public datasetRS: DatasetRecordService,
		public datasetACS: DatasetActionContainerService,
		protected datasetNavigatorService: DatasetNavigatorService,
		public datasetService: DatasetService,
		public datasetMCS: DatasetMultilanguageContainerService
	){
		super(datasetACS, datasetNavigatorService);
	}

	ngOnInit(): void{
		this.datasetACS.init({
			datasetCode: this.stepConfig.options.datasetCode,
			actionCode: 'edit',
			parentDatasetACS: this.parentDatasetACS,
			parentDatasetRS: this.parentDatasetRS,
			fetchData: true
		});

		this.datasetACS.ready
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(ready => {
			if(!ready) return;
			this.datasetMCS.init({
				datasetACS: this.datasetACS,
				datasetRS: this.datasetRS,
				parentForeignKey: this.stepConfig.options.parentForeignKey,
				parentValueKey: this.stepConfig.options.parentValueKey
			});
			this.datasetMCS.fetchRecord();
		});

		this.parentDatasetRS.record
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(record => {
			if(record) this.datasetMCS.activeLanguageCodes = record.active_language_codes;
		});

		// testi dell'entità aperta con l'assistente (pacchetto, categoria, destinazione, …; non il catalogo pubblico)
		const entity = this.assistantContextService.entity.value;
		if(entity && entity.type === this.parentDatasetACS?.datasetCode && entity.id == this.parentDatasetRS?.recordId) this.initAssistant();
	}

	ngOnDestroy(): void{
		if(this.assistantSnapshot) this.assistantContextService.clearSnapshot('descriptions_step');
		super.ngOnDestroy();
	}

	/**
	 * L'assistente vede la lingua aperta e le modifiche non salvate (unsaved_changes: il contenitore lo usa nell'avviso
	 * dopo una conferma); dopo una conferma il form si riallinea al server (anche le lingue attive).
	 */
	private initAssistant(): void{
		this.assistantSnapshot = true;
		this.assistantContextService.setSnapshot('descriptions_step', () => ({
			step: 'descriptions',
			lang: this.appService.language.value,
			unsaved_changes: !!this.formViewer?.formGroup?.dirty
		}));
		this.assistantService.entityChanged
		.pipe(
			takeUntil(this._unsubscribeAll),
			filter(entity => entity.type === this.parentDatasetACS.datasetCode && entity.id == this.parentDatasetRS.recordId)
		)
		.subscribe(() => {
			this.datasetMCS.fetchRecord();
			this.datasetMCS.fetchAll();
		});
	}

	onSave(): void{
		this.formViewer.formGroup.disable();
		let formData = this.formViewer.getFormData();
		const actionConfig = this.datasetACS.datasetConfig.defaultActions.create;
		if(actionConfig && typeof( actionConfig.getCallParams) === 'function'){
			formData = actionConfig.getCallParams(this.datasetACS, null, formData);
		}
		this.datasetMCS.save(formData)
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe({
			next: response => {
				if(this.formViewer){
					this.formViewer.formGroup.enable();
				}
				// for reaload active language when a new one is created
				if(!this.datasetRS.recordId){
					this.datasetACS.parentDatasetRS.reload();
					this.datasetMCS.fetchAll();
				}

				if(this.hasNext){
					setTimeout(() => {
						this.goNextStep();
					}, 200);
				}else if(response.id){
					this.datasetRS.recordId = response.id;
					this.datasetRS.reload();
				}
				
			},
			error: response => {
				if(response && response.error && response.error.errors) this.formErrors = response.error.errors;
				else this.formErrors = null;
				
				if(this.formViewer){
					this.formViewer.formGroup.enable();
					this.formViewer.setErrors(this.formErrors);
				}
			}
		});
	}
}
